import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import {
  renderInterestIcon,
  renderLanguagePrefix,
  renderSkillIcon,
} from '../components/icons/PortfolioIcons';
import {
  getCssLinearGradientForText,
  getGlowColorHex,
  getGradientPreset,
  hexToRgba,
  resolveSkillGradientKey,
} from '../utils/skillBrandColors';
import SkillGradientDefs from '../components/skills/SkillGradientDefs';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SkillsSection = styled.section`
  margin-bottom: 3rem;
`;

const NewEraSection = styled(SkillsSection)`
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.52) 0%,
    rgba(22, 0, 0, 0.82) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  padding: 1.2rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const NewEraTitle = styled(SectionTitle)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const NewEraBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1.5rem;
  text-shadow: 0px 0px 9px black;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 1rem;
  }
`;

const skillTextGradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const SkillCard = styled(motion.div) <{ $brandColor: string }>`
  --skill-brand: ${({ $brandColor }) => $brandColor};
  --skill-glow: ${({ $brandColor }) => hexToRgba($brandColor, 0.5)};
  background: linear-gradient(-360deg, rgb(93 0 0 / 16%) 0%, rgb(255 0 0 / 34%) 100%);
  border-radius: 10px;
  padding: 1rem 0.8rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  text-align: center;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: ${({ theme }) => theme.colors.text};
  transform: scale(1);
  transform-origin: center center;
  transition:
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.55s ease,
    border-color 0.45s ease;

  &:hover {
    transform: scale(1.07) translateY(-3px);
    z-index: 2;
    border-color: ${({ $brandColor }) => hexToRgba($brandColor, 0.35)};
    box-shadow:
      0 4px 18px ${({ $brandColor }) => hexToRgba($brandColor, 0.38)},
      0 14px 40px ${({ $brandColor }) => hexToRgba($brandColor, 0.22)},
      0 0 0 1px ${({ $brandColor }) => hexToRgba($brandColor, 0.1)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: box-shadow 0.35s ease, border-color 0.35s ease;

    &:hover {
      transform: none;
    }
  }
`;

const SkillName = styled.h3<{ $gradient: string; $shiftSec: number }>`
  font-size: 1.1rem;
  margin: 0;
  line-height: 1.25;
  font-weight: 700;
  background-image: ${({ $gradient }) => $gradient};
  background-size: 220% 220%;
  background-position: 0% 50%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${skillTextGradientShift} ${({ $shiftSec }) => $shiftSec}s ease infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background-position: 50% 50%;
  }
`;

const SkillIcon = styled.div<{ $gradKey: string }>`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.55s ease;


  svg {
    display: block;
    fill: ${({ $gradKey }) =>
    ['cursor', 'documentation', 'xd', 'mcpa2a', 'cursorskills'].includes($gradKey)
      ? 'none'
      : `url(#skill-grad-${$gradKey})`};
    stroke: url(#skill-grad-${({ $gradKey }) => $gradKey});
    filter: drop-shadow(0px 0px 7px black);
  }

  svg [fill='none'] {
    fill: none !important;
  }

  svg [stroke='none'] {
    stroke: none !important;
  }

  svg [stroke] {
    stroke: url(#skill-grad-${({ $gradKey }) => $gradKey});
  }

  svg [fill]:not([fill='none']) {
    fill: ${({ $gradKey }) =>
    ['cursor', 'documentation', 'xd', 'mcpa2a', 'cursorskills'].includes($gradKey)
      ? 'currentColor'
      : `url(#skill-grad-${$gradKey})`};
  }

  ${SkillCard}:hover & {
    filter: drop-shadow(0 0 14px var(--skill-glow));
  }

  @media (prefers-reduced-motion: reduce) {
    ${SkillCard}:hover & {
      filter: none;
    }
  }
`;

// Modal Overlay (fondo oscuro)
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

// Contenedor del Modal
const ModalContainer = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 10px;
  padding: 1.5rem;
  min-width: 300px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
`;

// Título del Modal
const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 0;
  margin-bottom: 1rem;
  padding-right: 2rem;
`;

// Contenido del Modal
const ModalContent = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
`;

// Botón para cerrar el Modal
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const InterestsSection = styled.section`
  margin-top: 4rem;
`;

const InterestsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const InterestItem = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const LanguageSection = styled.section`
  margin-top: 4rem;
`;

const LanguagesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const LanguageItem = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  min-width: 200px;
  flex: 1;
`;

const LanguageName = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: 0.35rem;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const LanguageLevel = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

// Componente Modal
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, content }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Manejar clic fuera del modal para cerrarlo
  const handleClickOutside = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Manejar la tecla ESC para cerrar el modal
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Evita saltos de layout al ocultar el scrollbar
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen, onClose]);

  // Variante de animación para el overlay
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  // Variante de animación para el modal
  const modalVariants = {
    hidden: { opacity: 0, y: -50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', damping: 25, stiffness: 300 }
    },
    exit: {
      opacity: 0,
      y: 50,
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };

  // Usar un portal para renderizar el modal directamente en el body
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          onClick={handleClickOutside}
        >
          <ModalContainer
            ref={modalRef}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>&times;</CloseButton>
            <ModalTitle>{title}</ModalTitle>
            <ModalContent>{content}</ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body // Renderizar en el body, no en el componente
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

const SkillsPage: React.FC = () => {
  const [isMobile, setisMobile] = useState(window.innerWidth <= 768);
  const [activeSkill, setActiveSkill] = useState<{ name: string; description: string } | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => {
      setisMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { t } = useTranslation();

  const frontendSkills = t('skills.frontend_skills', { returnObjects: true }) as any[];
  const backendSkills = t('skills.backend_skills', { returnObjects: true }) as any[];
  const toolSkills = t('skills.tool_skills', { returnObjects: true }) as any[];
  const newEraSkills = t('skills.new_era_skills', { returnObjects: true }) as any[];
  const interestItems = t('skills.interest_items', { returnObjects: true }) as any[];
  const languageItems = t('skills.language_items', { returnObjects: true }) as any[];

  function showIconLanguage(): React.ReactNode {
    if (!isMobile) {
      return null;
    }
    return renderLanguagePrefix();
  }

  // Función para manejar el clic en una skill card
  const handleSkillClick = (skill: { name: string; description: string }) => {
    setActiveSkill(skill);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setActiveSkill(null);
  };

  return (
    <PageContainer ref={pageRef} as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
      <SkillGradientDefs />
      <PageTitle variants={itemVariants}>{t('skills.title')}</PageTitle>

      <NewEraSection>
        <NewEraTitle variants={itemVariants}>
          {t('skills.new_era')}
          <NewEraBadge>{t('skills.new_era_badge')}</NewEraBadge>
        </NewEraTitle>
        <SkillsGrid>
          {newEraSkills.map((skill: any, index: number) => (
            <SkillCard
              key={index}
              $brandColor={getGlowColorHex(skill.name)}
              variants={itemVariants}
              onClick={() => handleSkillClick({
                name: skill.name,
                description: skill.description || t('skills.no_description')
              })}
            >
              <SkillIcon $gradKey={resolveSkillGradientKey(skill.name)}>
                {renderSkillIcon(skill.name)}
              </SkillIcon>
              <SkillName
                $gradient={getCssLinearGradientForText(skill.name)}
                $shiftSec={getGradientPreset(skill.name).textShiftSec}
              >
                {skill.name}
              </SkillName>
            </SkillCard>
          ))}
        </SkillsGrid>
      </NewEraSection>

      <SkillsSection>
        <SectionTitle variants={itemVariants}>{t('skills.frontend')}</SectionTitle>
        <SkillsGrid>
          {frontendSkills.map((skill: any, index: number) => (
            <SkillCard
              key={index}
              $brandColor={getGlowColorHex(skill.name)}
              variants={itemVariants}
              onClick={() => handleSkillClick({
                name: skill.name,
                description: skill.description || t('skills.no_description')
              })}
            >
              <SkillIcon $gradKey={resolveSkillGradientKey(skill.name)}>
                {renderSkillIcon(skill.name)}
              </SkillIcon>
              <SkillName
                $gradient={getCssLinearGradientForText(skill.name)}
                $shiftSec={getGradientPreset(skill.name).textShiftSec}
              >
                {skill.name}
              </SkillName>
            </SkillCard>
          ))}
        </SkillsGrid>
      </SkillsSection>

      <SkillsSection>
        <SectionTitle variants={itemVariants}>{t('skills.backend')}</SectionTitle>
        <SkillsGrid>
          {backendSkills.map((skill: any, index: number) => (
            <SkillCard
              key={index}
              $brandColor={getGlowColorHex(skill.name)}
              variants={itemVariants}
              onClick={() => handleSkillClick({
                name: skill.name,
                description: skill.description || t('skills.no_description')
              })}
            >
              <SkillIcon $gradKey={resolveSkillGradientKey(skill.name)}>
                {renderSkillIcon(skill.name)}
              </SkillIcon>
              <SkillName
                $gradient={getCssLinearGradientForText(skill.name)}
                $shiftSec={getGradientPreset(skill.name).textShiftSec}
              >
                {skill.name}
              </SkillName>
            </SkillCard>
          ))}
        </SkillsGrid>
      </SkillsSection>

      <SkillsSection>
        <SectionTitle variants={itemVariants}>{t('skills.tools')}</SectionTitle>
        <SkillsGrid>
          {toolSkills.map((tool: any, index: number) => (
            <SkillCard
              key={index}
              $brandColor={getGlowColorHex(tool.name)}
              variants={itemVariants}
              onClick={() => handleSkillClick({
                name: tool.name,
                description: tool.description || t('skills.no_description')
              })}
            >
              <SkillIcon $gradKey={resolveSkillGradientKey(tool.name)}>
                {renderSkillIcon(tool.name)}
              </SkillIcon>
              <SkillName
                $gradient={getCssLinearGradientForText(tool.name)}
                $shiftSec={getGradientPreset(tool.name).textShiftSec}
              >
                {tool.name}
              </SkillName>
            </SkillCard>
          ))}
        </SkillsGrid>
      </SkillsSection>

      <InterestsSection>
        <SectionTitle variants={itemVariants}>{t('skills.interests')}</SectionTitle>
        <InterestsList>
          {interestItems.map((interest: string, index: number) => (
            <InterestItem key={index} variants={itemVariants}>
              {renderInterestIcon(index)} {interest}
            </InterestItem>
          ))}
        </InterestsList>
      </InterestsSection>

      <LanguageSection>
        <SectionTitle variants={itemVariants}>{t('skills.languages')}</SectionTitle>
        <LanguagesList>
          {languageItems.map((language: any, index: number) => (
            <LanguageItem key={index} variants={itemVariants}>
              <LanguageName>
                {showIconLanguage()}
                {language.name}
              </LanguageName>
              <LanguageLevel>{language.level}</LanguageLevel>
            </LanguageItem>
          ))}
        </LanguagesList>
      </LanguageSection>

      {/* Modal para mostrar los detalles de la habilidad */}
      <Modal
        isOpen={activeSkill !== null}
        onClose={handleCloseModal}
        title={activeSkill?.name || ''}
        content={activeSkill?.description || ''}
      />
    </PageContainer>
  );
};

export default SkillsPage; 